/**
 * Horse Race browser-source entrypoint.
 *
 * URL params:
 *   key  — API key (required for non-mock mode)
 *   ws   — WS port (default 3001) for local dev
 *   mock=1 — skip WS and run a scripted demo (Phase 1: just renders the lobby)
 */

import type { LbRow, OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { BettingState, SceneName } from './state/types.js';
import { createDomStage } from './scene/lib/stage.js';
import { applyTheme, DEFAULT_THEME } from './ui/theme.js';
import { createLobby } from './scene/lobby.js';
import { createBetting } from './scene/betting.js';
import { createCountdown as createRaceCountdownScene } from './scene/countdown.js';
import { createTrack } from './scene/track.js';
import { createResults } from './scene/results.js';
import { createPodium } from './scene/podium.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { audioBus } from './audio/bus.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { mountFullscreenButton } from './ui/fullscreen-button.js';
import { createAnimatedBg } from './ui/animated-bg.js';
import { createSparkles } from './ui/sparkles.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createCountdown } from './ui/countdown.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source — used to filter `cross_game_conflict` events. */
const MY_SLUG = 'horse-race';

applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
// Viewer mode (`?viewer=1&handle=<HANDLE>`) routes through the public read-only
// /watch/<handle>/feed socket and suppresses streamer-only chrome.
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

// Viewer-aware audio facades — see survey-says/main.ts for rationale. All
// sfx + music calls become silent no-ops in viewer mode so the AudioContext
// is never instantiated and Chromium never logs the autoplay-blocked warning.
const sfx: typeof sfxImpl = isViewer
  ? ({
      preload: async () => {},
      chime: () => {},
      bell: () => {},
      cheer: () => {},
      bet: () => {},
      pistol: () => {},
      countdown: () => {},
      startCrowdLoop: () => {},
      stopCrowdLoop: () => {},
      stopGallopLoop: () => {},
    } as unknown as typeof sfxImpl)
  : sfxImpl;
const music: typeof musicImpl = isViewer
  ? ({
      play: () => {},
      stop: () => {},
      fadeOut: () => {},
    } as unknown as typeof musicImpl)
  : musicImpl;

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.cssText = 'position: relative; width: 100vw; height: 100vh; overflow: hidden;';
root.setAttribute('data-testid', 'browser-root');

const stage = createDomStage({ mount: root });
const stageEl = stage.el;

// Animated background sits behind every scene inside the stage.
const animatedBg = createAnimatedBg(stageEl);
void animatedBg;

const lobby = createLobby(stageEl);
const betting = createBetting(stageEl);
const raceCountdown = createRaceCountdownScene(stageEl);
const track = createTrack(stageEl);
const results = createResults(stageEl);
const podium = createPodium(stageEl);
const connectionLost = createConnectionLost(stageEl);

// Cross-scene overlays (countdown + side leaderboard + sparkles).
const sideLeaderboard = createSideLeaderboard(stageEl);
const countdown = createCountdown(stageEl);
const sparkles = createSparkles(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);
sideLeaderboard.setVisible(false);
countdown.hide();

if (!isViewer) {
  mountFullscreenButton();
  mountAudioUnlock();
  // Audio: pre-fetch + decode SFX buffers; lobby bed starts immediately so OBS
  // page-permissions kick it off without a gesture. If autoplay is blocked the
  // HTMLAudio play() promise rejects silently and music.resumeIfPending() runs
  // after the first gesture.
  void sfx.preload();
  music.play('lobby');
}

// Scene → handles map. Each scene knows how to setVisible(true/false). When
// a scene is hidden the cross-scene overlays may also need to dim.
const allScenes: Record<SceneName, { setVisible(v: boolean): void }> = {
  lobby,
  betting,
  countdown: raceCountdown,
  track,
  results,
  podium,
  connection_lost: connectionLost,
};

function applyScene(next: SceneName): void {
  for (const [name, h] of Object.entries(allScenes)) {
    h.setVisible(name === next);
  }
  // Countdown overlay sits ON TOP of the track — keep the track scene rendered
  // underneath so viewers see horses at the start gate while the digits tick
  // down. The countdown CSS uses a semi-transparent vignette (countdown.ts) so
  // the lanes/finish line/horse art shows through.
  if (next === 'countdown') {
    track.setVisible(true);
  }
  // Side leaderboard policy (Horse Race specific): show during BETTING and
  // PODIUM only. Hidden during the race itself (let the track command full
  // attention) and during RESULTS (winners take centre-stage). Lobby has its
  // own dedicated panels; connection-lost hides everything.
  const showSide = next === 'betting' || next === 'podium';
  sideLeaderboard.setVisible(showSide);
  // Race phase has NO countdown clock — only the betting phase keeps it.
  // The pre-race COUNTDOWN scene draws its OWN big "5..4..3..2..1..GO" overlay
  // (createRaceCountdownScene), so the betting timer ring stays hidden then too.
  if (next !== 'betting') countdown.hide();
  // Big celebration moments fire confetti on the next paint.
  if (next === 'podium') {
    sparkles.confetti({ x: 960, y: 300, count: 220 });
    setTimeout(() => sparkles.confetti({ x: 960, y: 300, count: 160 }), 700);
  }
  // Music routing:
  //   lobby   -> lobby bed (looping)
  //   betting -> race bed  (anticipation, looping)
  //   track   -> fade music out so gallop loop + cheer dominate
  //   results -> stop music; the cheer SFX is enough
  //   podium  -> race bed back in for celebratory feel
  if (next === 'lobby') {
    music.play('lobby');
  } else if (next === 'betting') {
    music.play('race');
  } else if (next === 'countdown') {
    // Keep race music bed running under the countdown — it builds anticipation
    // and the pistol/beep cues sit cleanly on top.
    music.play('race');
  } else if (next === 'track') {
    music.fadeOut(500);
  } else if (next === 'podium') {
    music.play('race');
  } else {
    music.stop();
  }
  // Gallop loops are started PER-HORSE inside track.setTrack (one loop per
  // lane at 0.2 each, fading out as that horse crosses the line). Crowd
  // ambience runs as a single loop alongside, lifted to 0.7 so the stadium
  // murmur matches the energy of 8 stacked gallop loops.
  if (next === 'track') {
    sfx.startCrowdLoop(0.7, 600);
  } else {
    sfx.stopGallopLoop(400);
    sfx.stopCrowdLoop(600);
  }
  root.setAttribute('data-scene', next);
  (window as unknown as { __hr_scene?: SceneName }).__hr_scene = next;
}

// Sentinel "far future" for parked horses during the countdown overlay.
const PARKED_HORSE_TIME_MS = 999_999;

/** Park horses at the start gate while the countdown digits tick. */
function seedTrackForCountdown(raceId: string, betting: BettingState, endsAtMs: number): void {
  const horses = betting.horses.map((h) => ({
    ...h,
    timeAt25Ms: PARKED_HORSE_TIME_MS,
    timeAt50Ms: PARKED_HORSE_TIME_MS,
    timeAt75Ms: PARKED_HORSE_TIME_MS,
    timeAtFinishMs: PARKED_HORSE_TIME_MS,
    finishPlace: 0,
  }));
  track.setTrack({
    raceId,
    raceIndex: betting.raceIndex,
    racesTotal: betting.racesTotal,
    startAtMs: endsAtMs,
    raceDurationMs: 30_000,
    horses,
  });
}

const store = new Store();
applyScene(store.getState().scene);

// Per-bet ping: hr_emoji_cheer fires once per actual bet so we use that as the
// signal. Throttled to ~3/sec so a chat-storm doesn't drown out the music.
const BET_PING_MIN_MS = 333;
let lastBetPingAt = 0;
// Drives sfx.bet() ticks during the last 5s of the betting countdown.
let bettingEndsAt = 0;
let lastBettingTickSecond = -1;

function maybePingBet(): void {
  const now = performance.now();
  if (now - lastBetPingAt < BET_PING_MIN_MS) return;
  lastBetPingAt = now;
  sfx.bet();
}

// --- Countdown audio scheduler ---------------------------------------------
// Tracks pending setTimeout handles so a fresh `hr_race_countdown` cancels any
// stale timers from a previous race (defensive — the FSM never overlaps races,
// but the page can reload mid-countdown).
let countdownAudioTimers: ReturnType<typeof setTimeout>[] = [];

function clearCountdownAudio(): void {
  for (const t of countdownAudioTimers) clearTimeout(t);
  countdownAudioTimers = [];
}

function scheduleCountdownAudio(_startedAtMs: number, endsAtMs: number): void {
  clearCountdownAudio();
  // We schedule beeps at each integer-second boundary BEFORE endsAtMs (the
  // moment the visible "GO!" lands). For a 5s countdown this fires at
  // T-5s/-4s/-3s/-2s/-1s relative to endsAtMs, then the pistol at T+0.
  const now = Date.now();
  // Find the largest integer K such that endsAtMs - K*1000 >= now, then walk
  // K..1 inclusive scheduling a beep each.
  for (let k = 10; k >= 1; k--) {
    const fireAt = endsAtMs - k * 1000;
    if (fireAt < now) continue;
    const delay = fireAt - now;
    countdownAudioTimers.push(setTimeout(() => sfx.countdown(), delay));
  }
  // Pistol on GO!.
  const pistolDelay = Math.max(0, endsAtMs - now);
  countdownAudioTimers.push(setTimeout(() => sfx.pistol(), pistolDelay));
}

let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

function handleWs(msg: OutMsg): void {
  store.dispatchWs(msg);
  switch (msg.type) {
    case 'hr_betting_open': {
      sfx.chime();
      // Betting phase still shows a countdown — viewers need to know when bets close.
      // countdown_starts_at_ms (when set + future) holds the visible timer at
      // the full duration until the broadcast-relay grace passes, so viewers
      // see the FULL betting window on screen (not duration - delay).
      const skewToLocal = performance.now() - Date.now();
      const endsLocal = msg.ends_at_ms + skewToLocal;
      const startsLocal = msg.countdown_starts_at_ms != null
        ? msg.countdown_starts_at_ms + skewToLocal
        : undefined;
      countdown.setPhase('BET', endsLocal, startsLocal);
      bettingEndsAt = msg.ends_at_ms;
      lastBettingTickSecond = -1;
      break;
    }
    case 'hr_race_countdown': {
      // Pre-race countdown overlay phase. Schedule a beep on each tick
      // (5,4,3,2,1) and a pistol on GO!, anchored to the wall-clock end.
      // The scene itself renders the big number; this side-effect chain
      // handles audio.
      countdown.hide();
      bettingEndsAt = 0;
      scheduleCountdownAudio(msg.started_at_ms, msg.ends_at_ms);
      break;
    }
    case 'hr_race_track':
      // Pistol/bell already fired on GO! during the countdown phase. If the
      // streamer disabled the countdown (race_countdown_seconds=0) we still
      // need a starting cue, so play the bell unconditionally — the buffer
      // is cheap to re-trigger.
      sfx.bell();
      // Race phase has NO timer — viewers just watch the horses run.
      countdown.hide();
      bettingEndsAt = 0;
      break;
    case 'hr_race_results':
      sfx.cheer();
      countdown.hide();
      sparkles.burst({ x: 960, y: 480, count: 60, speed: 1.2 });
      break;
    case 'hr_game_over':
      sfx.chime();
      countdown.hide();
      break;
    case 'hr_emoji_cheer':
      // Float a glyph up the bettor's lane. Visual only — the deterministic
      // simulator already factored bet shares into the timing curve at race
      // start (see backend simulator handicap).
      track.pushCheer(msg.lane, msg.emoji);
      // Per-bet audio ping — throttled so a chat-storm doesn't dominate.
      maybePingBet();
      break;
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'state_snapshot': {
      const snapSettings = (msg as any).state?.settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      break;
    }
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  if (next.theme && next.theme !== prev.theme) applyTheme(next.theme);
  // Lobby panels — all-time leaderboard data feeds top + tail panels.
  if (next.scene === 'lobby') {
    lobby.setLeaderboard(next.leaderboard, next.leaderboardAllTime);
  }
  // Side leaderboard: feed both views. All-time = leaderboardAllTime (top 200);
  // session = the snapshot's leaderboard_top10 which the backend resets per game.
  if (next.leaderboardAllTime !== prev.leaderboardAllTime) {
    sideLeaderboard.setAllTimeRows(next.leaderboardAllTime);
  }
  if (next.leaderboard !== prev.leaderboard) {
    // Reuse the top10 as the session view rows — same {rank, player_id, ...}
    // shape; renaming to total_points is already in the snapshot.
    sideLeaderboard.setSessionRows(next.leaderboard);
  }
  if (next.scene === 'betting' && next.betting) {
    betting.setBetting(next.betting);
  }
  if (next.scene === 'countdown' && next.countdown) {
    raceCountdown.setCountdown(next.countdown);
    // Seed the track scene with horses parked at the start gate so the
    // countdown overlay has a visible race course behind it. progressFor()
    // returns 0 when elapsed <= 0; once hr_race_track lands the real curve
    // replaces this seed.
    if (next.betting) seedTrackForCountdown(next.countdown.raceId, next.betting, next.countdown.endsAtMs);
  }
  if (next.scene === 'track' && next.track) {
    track.setTrack(next.track);
  }
  if (next.scene === 'results' && next.results) {
    results.setResults(next.results);
  }
  if (next.scene === 'podium' && next.podium) {
    podium.setPodium(next.podium);
  }
});

// Drive the betting countdown ring at 5Hz; drive the track render at ~60Hz
// via requestAnimationFrame.
setInterval(() => {
  if (store.getState().scene === 'betting') betting.tick();
}, 200);

// Tick SFX driver: fires sfx.bet() blip once per second in the final 5s of
// the betting window, so viewers hear the urgency without the chime/buzz mix.
setInterval(() => {
  if (bettingEndsAt <= 0) return;
  const remainingMs = bettingEndsAt - Date.now();
  if (remainingMs <= 0 || remainingMs > 5_000) return;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  if (secondsLeft !== lastBettingTickSecond && secondsLeft >= 1 && secondsLeft <= 5) {
    lastBettingTickSecond = secondsLeft;
    sfx.bet();
  }
}, 200);

function rafLoop(): void {
  const scene = store.getState().scene;
  if (scene === 'track') track.tick();
  else if (scene === 'countdown') raceCountdown.tick();
  requestAnimationFrame(rafLoop);
}
requestAnimationFrame(rafLoop);

// --- Connect ---
function buildMockRow(rank: number, name: string, points: number, wins: number): LbRow {
  return {
    rank,
    player_id: rank,
    display_name: name,
    avatar_seed: name.toLowerCase(),
    total_points: points,
    games_played: 12 - rank,
    games_won: wins,
  };
}

let wsClient: WsClient | null = null;
function connectWs(): void {
  // Viewer mode → /watch/<handle>/feed (read-only). Streamer mode keeps the
  // existing /browser?key=...&game=horse-race handshake.
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort), { game: 'horse-race' });
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: handleWs,
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      // The watch SPA owns the connection-lost UI for viewer iframes.
      if (!isViewer) connectionLost.setVisible(s !== 'open');
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__hr_ws = wsClient;
}

if (mockMode) {
  const fakeRows: LbRow[] = [
    buildMockRow(1, 'AlphaWolf', 1240, 4),
    buildMockRow(2, 'BetaTester', 980, 1),
    buildMockRow(3, 'GammaRay', 720, 0),
  ];
  lobby.setLeaderboard(fakeRows, fakeRows);
  void audioBus.unlock();
} else {
  connectWs();
}

// Auto-pause when browser source is not visible. OBS scene switch / tab bg
// → close WS gracefully + stop audio loops. Backend soft-abort grace then
// ends the game once all sources for this tenant+slug are gone. Re-show
// reconnects (FSM re-broadcasts state via state_snapshot).
if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
      // Stop loops so a hidden tab doesn't keep hooves drumming inaudibly.
      sfx.stopGallopLoop(120);
      sfx.stopCrowdLoop(120);
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

if (!isViewer) {
  window.addEventListener(
    'pointerdown',
    () => {
      void audioBus.unlock();
    },
    { once: true },
  );
}

(window as unknown as Record<string, unknown>).__hr_store = store;
(window as unknown as Record<string, unknown>).__hr_sparkles = sparkles;
// Exposed for the playtest harness so it can simulate hr_emoji_cheer side
// effects (handleWs is the path that floats glyphs; the store reducer alone
// doesn't dispatch the visual). Production never reads this.
(window as unknown as Record<string, unknown>).__hr_handleWs = handleWs;
(window as unknown as Record<string, unknown>).__hr_track = track;
(window as unknown as Record<string, unknown>).__hr_countdown_scene = raceCountdown;
